import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Flex,
  Icon,
  Markdown,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

import type {
  Commonv1Value,
  V1AutomationConfig,
  V1CredentialInfo,
  V1Step,
} from '@/generated/hooks/grimoire/models';
import { resolveStepConfigs } from '@/modules/automation/utils/resolve-step-config';
import { resolveStepConnections } from '@/modules/automation/utils/resolve-step-connections';
import type { TStepRunStatus } from '@/modules/automation/utils/step-run-status';
import type { IStepRunIO } from '@/modules/automation/hooks/useFocusedTestRunStatus';
import { OutputRenderer } from '@/modules/outputs/components/OutputRenderer';
import { isBrowserInstance } from '@/modules/outputs/utils';
import { RunLogInputsContent } from '@/modules/automation-run/components/run-log/RunLogInputsContent';
import { ExternalCallsSection } from '@/modules/automation-run/components/run-log/ExternalCallsSection';
import { formatRunError } from '@/modules/automation-run/utils/format-run-error';

import { ManifestClampedText } from './ManifestClampedText';
import { ManifestConfigTab } from './ManifestConfigTab';
import { ManifestConnectionsTab } from './ManifestConnectionsTab';

const EMPTY_CREDENTIALS_BY_ALIAS = new Map<string, V1CredentialInfo>();

/** Duration (ms) of a step body's expand/collapse height animation — shared
 *  with `ManifestSopView`'s auto-follow effect so its recentering scroll can
 *  wait for this to finish instead of racing it (see the effect there for why
 *  that race is the thing that made the transition stutter). */
export const STEP_BODY_ANIM_MS = 200;

interface IManifestStepCardProps {
  step: V1Step;
  isOpen: boolean;
  onToggle: () => void;
  /** Omits the rail connector below this card — it's the last step in its
   *  phase group. */
  isLast?: boolean;
  /** Automation-level business-tunable config, keyed by name. Resolved
   *  against this step's `audit.config_accesses` to populate its Config tab. */
  automationConfig?: V1AutomationConfig;
  /** Persists an edited config value. Omitted (e.g. diff mode) means the
   *  step has no config accesses to resolve, so the tab never renders. */
  onSaveConfigValue?: (name: string, value: Commonv1Value) => Promise<void>;
  /** The automation's extracted code connections, keyed by connection
   *  alias — resolves each external call's book/icon for the Connections tab. */
  credentialsByAlias?: ReadonlyMap<string, V1CredentialInfo>;
  /** Toggles this step as a context chip in the chat (Cursor "add to chat").
   *  Omitted hides the affordance entirely — e.g. when no chat is visible. */
  onToggleStepInChat?: (step: V1Step) => void;
  /** Whether this step is currently referenced in the chat composer — drives
   *  the toggle button's pressed state. */
  isReferencedInChat?: boolean;
  /** Opens the step-test workbench for this step. Omitted hides the "Test
   *  step" affordance entirely (e.g. when the workbench entry is disabled). */
  onTestStep?: () => void;
  /** Live/historical test-run status for this step's rail-node icon. Omitted
   *  (plain authoring view) renders the plain step number. */
  status?: TStepRunStatus;
  /** This step's real inputs/output/duration from the focused test run's
   *  journal, once it has executed. Omitted when the step hasn't run yet or
   *  no run is focused. */
  io?: IStepRunIO;
}

// The step's one-line summary is the lead description of what it does, so
// it reads full-contrast, same as the title.
const STEP_TEXT_PROPS = { level: 'small', color: 'default' } as const;
// Rules are secondary, supporting detail — rendered as a blockquote-style
// callout (matching the AOP markdown doc's `.markdown-blockquote`: italic,
// muted, left border) so they read as quieter than the overview above them.
const RULES_TEXT_PROPS = { level: 'small', color: 'muted' } as const;

/** The run's exception, parsed into a headline + optional collapsible
 *  technical detail — mirrors the earlier `do_it_now` draft's
 *  `RunStepFooter`/`formatRunError` pair, restyled onto design-system
 *  primitives instead of raw `.run-io-error` CSS classes.
 *
 *  Two variants, both driven by the same parser: `'destructive'` for a
 *  terminal `RunState.failed` (nothing more will happen to this run) and
 *  `'warning'` for `RunState.awaiting_guidance` — which is itself always the
 *  result of a runtime exception too, but a recoverable one: during a test
 *  run there's no Astral subagent to auto-patch it, so Quill diagnoses it in
 *  chat and, once fixed, replays the run. `'warning'` gets an extra hint
 *  line making that distinction clear, so it doesn't read as a dead end. */
function StepError({
  errorText,
  variant,
}: {
  errorText: string;
  variant: 'destructive' | 'warning';
}) {
  const { t: tAutomation } = useTranslation('automation');
  const [isExpanded, setIsExpanded] = useState(false);
  const err = useMemo(
    () => formatRunError(errorText, tAutomation),
    [errorText, tAutomation],
  );
  const meta = [err.status, err.code].filter(Boolean).join(' · ');
  const isWarning = variant === 'warning';
  const textColorClassName = isWarning ? 'text-warning' : 'text-destructive';

  return (
    <Flex
      direction="column"
      gap={2}
      className={cn(
        'mt-3 w-full rounded-md border p-3',
        isWarning
          ? 'border-warning/30 bg-warning/10'
          : 'border-destructive/30 bg-destructive/10',
      )}
      data-testid="manifest-step-error"
    >
      <Flex align="center" gap={2}>
        <Icon
          type={isWarning ? 'AlertCircle' : 'TriangleAlert'}
          size="sm"
          className={cn('shrink-0', textColorClassName)}
        />
        <Text level="small" weight="medium" className={textColorClassName}>
          {err.headline}
        </Text>
        {meta && (
          <Text level="xSmall" className={textColorClassName}>
            ({meta})
          </Text>
        )}
      </Flex>
      {isWarning && (
        <Text level="xSmall" color="muted">
          {tAutomation('automation.manifestSopView.step.awaitingGuidanceHint')}
        </Text>
      )}
      {err.truncated && (
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <Flex
              role="button"
              tabIndex={0}
              align="center"
              gap={1}
              className="w-fit cursor-pointer"
              data-testid="manifest-step-error-trigger"
            >
              <Icon
                type="ChevronRight"
                size="xs"
                className={cn(
                  textColorClassName,
                  'transition-transform',
                  isExpanded && 'rotate-90',
                )}
              />
              <Text level="xSmall" className={textColorClassName}>
                {tAutomation(
                  'automation.manifestSopView.step.technicalDetails',
                )}
              </Text>
            </Flex>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Text
              level="xSmall"
              color="muted"
              className="mt-2 block w-full min-w-0 break-words whitespace-pre-wrap font-mono"
            >
              {err.detail}
            </Text>
          </CollapsibleContent>
        </Collapsible>
      )}
    </Flex>
  );
}

/** This step's real inputs/output/duration from the focused test run, shown
 *  directly beneath its docstring content once the step has executed —
 *  reuses the same chip/output renderers `ManifestRunLog` uses on the real run
 *  page, so a test run's results look identical to a real one's. */
function StepResult({
  io,
  status,
}: {
  io: IStepRunIO;
  status?: TStepRunStatus;
}) {
  const { t: tAutomation } = useTranslation('automation');
  const chipInputEntries = io.inputs.filter(
    ([, value]) => !isBrowserInstance(value),
  );
  const hasInputs = chipInputEntries.length > 0;
  const hasOutput = io.output != null;
  const externalCalls = io.externalCalls ?? [];
  const hasExternalCalls = externalCalls.length > 0;

  // A step that completed and produced nothing is not the same as a step that
  // never ran, but both used to render as empty space. On success the block
  // always renders so it can say "No output" explicitly. A failed step with no
  // I/O at all is left to `StepError` alone — an empty result box beside an
  // error adds nothing.
  const hasResult =
    hasInputs || hasOutput || hasExternalCalls || status === 'done';
  if (!hasResult && !io.errorText) return null;

  /** Header suffix — a duration once finished, otherwise the state itself. */
  const statusLabel =
    status === 'running'
      ? tAutomation('automation.manifestSopView.step.resultRunning')
      : status === 'failed'
        ? tAutomation('automation.manifestSopView.step.resultFailed')
        : status === 'paused'
          ? tAutomation('automation.manifestSopView.step.resultPaused')
          : io.durationLabel;

  return (
    // min-w-0 down this chain, or one wide output value widens every ancestor.
    <Flex direction="column" className="w-full min-w-0">
      {hasResult && (
        <Flex
          direction="column"
          gap={3}
          className="mt-3 w-full min-w-0 rounded-md border border-border bg-muted/40 p-3"
          data-testid="manifest-step-result"
        >
          <Flex align="center" gap={2}>
            <Text level="small" weight="medium">
              {tAutomation('automation.manifestSopView.step.result')}
            </Text>
            {statusLabel && (
              <Text level="xSmall" color="muted">
                {statusLabel}
              </Text>
            )}
          </Flex>
          {hasInputs && (
            <Flex direction="column" gap={2} className="w-full min-w-0">
              <Text level="xSmall" color="muted">
                {tAutomation('automation.runLog.inputs')}
              </Text>
              <RunLogInputsContent entries={chipInputEntries} />
            </Flex>
          )}
          <ExternalCallsSection calls={externalCalls} labelTone="muted" />
          <Flex direction="column" gap={2} className="w-full min-w-0">
            <Text level="xSmall" color="muted">
              {tAutomation('automation.runLog.output')}
            </Text>
            {hasOutput ? (
              <OutputRenderer value={io.output} />
            ) : (
              <Text level="xSmall" color="muted">
                {/* Only a step that is over has produced nothing — done, or
                    failed and never getting there. One that is still running,
                    paused or not yet reached simply has not got there yet. */}
                {status === 'done' || status === 'failed'
                  ? tAutomation('automation.manifestSopView.step.outputNone')
                  : tAutomation(
                      'automation.manifestSopView.step.outputPending',
                    )}
              </Text>
            )}
          </Flex>
        </Flex>
      )}
      {io.errorText && (
        <StepError
          errorText={io.errorText}
          variant={status === 'paused' ? 'warning' : 'destructive'}
        />
      )}
    </Flex>
  );
}

function StepDetails({ step }: { step: V1Step }) {
  const rules = step.rules ?? [];

  return (
    <Flex direction="column" gap={2}>
      {step.overview && (
        <ManifestClampedText text={step.overview} textProps={STEP_TEXT_PROPS} />
      )}

      {rules.length > 0 && (
        // `width="full"`, not `Flex`'s default `w-auto`: this and each rule
        // below are flex items of a column parent, so an auto-width box here
        // shrink-wraps its content and can grow past the step — see the note
        // on the card root.
        <Flex direction="column" gap={2} width="full" className="min-w-0">
          {rules.map((rule, index) => (
            <Flex
              key={`${rule}-${index}`}
              direction="column"
              width="full"
              className="min-w-0 border-l-4 border-border pl-4 italic"
            >
              <Markdown textProps={RULES_TEXT_PROPS}>{rule}</Markdown>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
}

interface IManifestStepBodyProps {
  step: V1Step;
  /** Automation-level business-tunable config, keyed by name. Resolved
   *  against this step's `audit.config_accesses` to populate its Config tab. */
  automationConfig?: V1AutomationConfig;
  /** Persists an edited config value. Omitted hides the Config tab. */
  onSaveConfigValue?: (name: string, value: Commonv1Value) => Promise<void>;
  /** The automation's extracted code connections, keyed by connection
   *  alias — resolves each external call's book/icon for the Connections tab. */
  credentialsByAlias?: ReadonlyMap<string, V1CredentialInfo>;
  /** Live/historical test-run status for this step. */
  status?: TStepRunStatus;
  /** This step's real inputs/output/duration from the focused test run. */
  io?: IStepRunIO;
}

/** An open step's content: overview, rules, the focused run's result, and the
 *  Config/Connections tabs. */
export function ManifestStepBody({
  step,
  automationConfig,
  onSaveConfigValue,
  credentialsByAlias,
  status,
  io,
}: IManifestStepBodyProps) {
  const { t: tAutomation } = useTranslation('automation');
  const resolvedConfigs = onSaveConfigValue
    ? resolveStepConfigs(step.audit, automationConfig)
    : [];
  const configTabProps =
    onSaveConfigValue && resolvedConfigs.length > 0
      ? { configs: resolvedConfigs, onSave: onSaveConfigValue }
      : null;
  const showConfig = !!configTabProps;
  const resolvedConnections = resolveStepConnections(
    step.audit?.external_calls,
    credentialsByAlias ?? EMPTY_CREDENTIALS_BY_ALIAS,
  );
  const showConnections = resolvedConnections.length > 0;
  // '' means no tab selected. Controlled (rather than Tabs' own
  // defaultValue) so clicking the already-active tab can close it back to
  // that state. Radix's own onValueChange won't fire for that case — its
  // controlled-state helper diffs the incoming value against the current
  // one and no-ops when they're equal — so each trigger's onMouseDown
  // (which runs before Radix's internal handler) does the closing itself.
  const [activeTechTab, setActiveTechTab] = useState('');
  const handleTechTabMouseDown = (value: string) => () => {
    if (activeTechTab === value) setActiveTechTab('');
  };

  return (
    <>
      <StepDetails step={step} />
      {io && <StepResult io={io} status={status} />}
      {(showConfig || showConnections) && (
        <div className="mt-3 border-t border-border pt-3">
          <Tabs value={activeTechTab} onValueChange={setActiveTechTab}>
            <TabsList>
              {showConfig && (
                <TabsTrigger
                  value="config"
                  onMouseDown={handleTechTabMouseDown('config')}
                >
                  {tAutomation('automation.manifestSopView.step.config')}
                </TabsTrigger>
              )}
              {showConnections && (
                <TabsTrigger
                  value="connections"
                  onMouseDown={handleTechTabMouseDown('connections')}
                >
                  {tAutomation('automation.manifestSopView.step.connections')}
                </TabsTrigger>
              )}
            </TabsList>
            {configTabProps && (
              <TabsContent value="config" className="pt-2">
                <ManifestConfigTab {...configTabProps} />
              </TabsContent>
            )}
            {showConnections && (
              <TabsContent value="connections" className="pt-2">
                <ManifestConnectionsTab
                  connections={resolvedConnections}
                  automationConfig={automationConfig}
                />
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}
    </>
  );
}

/** One step, rendered as a rail node + collapsible card. Open/closed is owned
 *  by `ManifestSopView`, which tracks every open step independently — this
 *  card's own toggle never implies anything about its siblings. A closed step
 *  shows only its title/step-number. Sourced entirely from the manifest
 *  `Step`; no markdown parsing. */
export function ManifestStepCard({
  step,
  isOpen,
  onToggle,
  isLast,
  automationConfig,
  onSaveConfigValue,
  credentialsByAlias,
  onToggleStepInChat,
  isReferencedInChat,
  onTestStep,
  status,
  io,
}: IManifestStepCardProps) {
  const { t: tAutomation } = useTranslation('automation');
  const toggleStepInChatLabel = tAutomation(
    isReferencedInChat
      ? 'automation.manifestSopView.step.removeFromChat'
      : 'automation.manifestSopView.step.addToChat',
  );
  const testStepLabel = tAutomation('automation.stepWorkbench.testStep');
  const prefersReducedMotion = useReducedMotion();

  // No scroll anchoring here: steps open and close independently, so toggling
  // one never resizes a sibling above it and this header keeps its on-screen
  // position on its own. The body only ever grows/shrinks below the header.

  return (
    // `width="full"` matters here, and on every box between this and the step
    // prose. `Flex` defaults to `width: 'auto'` with `align: 'start'`, so an
    // unqualified `Flex` inside a column parent is a shrink-to-fit box — and
    // shrink-to-fit is `min(max(min-content, available), max-content)`, which
    // *grows past* the available width whenever min-content exceeds it. At the
    // document's 720px column a step has 580px for its prose, and anything
    // whose min-content beats that widens the box: a bare (non-backticked)
    // URL or identifier, measured at 671px against 640px available, or a wide
    // markdown table, 740px. Every line of prose in the step then wraps to the
    // widened box and runs past the document card's edge, which is the
    // reported symptom. `min-w-0 flex-1` on the row below does not help —
    // `min-width: 0` only lets a flex item shrink, it does not shrink the
    // min-content contribution that sizes an auto-width ancestor.
    //
    // Backticked code and fenced blocks were never affected: DS `Markdown`
    // gives both `break-anywhere`, which keeps their min-content small. Only
    // `p` and `a` have no break rule, hence `break-words` on the body below.
    //
    // Pinning to 100% caps the step at the content column; `break-words` then
    // wraps the long token inside that cap, so nothing is clipped either.
    <Flex
      gap={3}
      align="stretch"
      width="full"
      className="min-w-0"
      data-testid={`manifest-step-card-${step.number}`}
    >
      <Flex
        direction="column"
        align="center"
        className="automation-step-rail w-6 shrink-0"
      >
        <Flex
          align="center"
          justify="center"
          className={cn(
            'size-6 shrink-0 rounded-full border text-xs',
            isOpen
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border text-muted-foreground',
          )}
        >
          {status === 'running' ? (
            <Icon
              type="Loader2"
              size="xs"
              className="animate-spin"
              data-testid="manifest-step-status-running"
            />
          ) : status === 'done' ? (
            <Icon
              type="Check"
              size="xs"
              data-testid="manifest-step-status-done"
            />
          ) : status === 'failed' ? (
            <Icon
              type="X"
              size="xs"
              className="text-destructive"
              data-testid="manifest-step-status-failed"
            />
          ) : status === 'paused' ? (
            <Icon
              type="AlertCircle"
              size="xs"
              className="text-warning"
              data-testid="manifest-step-status-paused"
            />
          ) : (
            step.number
          )}
        </Flex>
        {!isLast && <div className="w-px flex-1 bg-border" aria-hidden />}
      </Flex>

      <Flex
        direction="column"
        gap={2}
        className="automation-step-row min-w-0 flex-1"
      >
        <Flex align="center" gap={1}>
          <Button
            type="button"
            variant="ghost"
            onClick={onToggle}
            // `text-start` (not `text-left`) so the title follows the
            // direction inherited from the SOP view.
            className="h-auto min-w-0 flex-1 justify-start px-2 py-0 text-start hover:bg-transparent"
            aria-expanded={isOpen}
          >
            <Text weight="semibold">
              {step.title ||
                tAutomation('automation.manifestSopView.step.untitled')}
            </Text>
          </Button>
          {onToggleStepInChat && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-pressed={!!isReferencedInChat}
                  className={cn(
                    'size-6 shrink-0 hover:bg-muted',
                    isReferencedInChat
                      ? 'text-primary hover:text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  aria-label={toggleStepInChatLabel}
                  onClick={() => onToggleStepInChat(step)}
                >
                  <Icon type="MessageSquare" size="sm" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{toggleStepInChatLabel}</TooltipContent>
            </Tooltip>
          )}
          {onTestStep && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="size-6 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={testStepLabel}
                  data-testid={`manifest-step-test-${step.number}`}
                  onClick={onTestStep}
                >
                  <Icon type="FlaskConical" size="sm" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{testStepLabel}</TooltipContent>
            </Tooltip>
          )}
        </Flex>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                duration: prefersReducedMotion ? 0 : STEP_BODY_ANIM_MS / 1000,
              }}
              // `overflow-hidden` here clips the height animation only. It
              // does nothing horizontally while the box is auto-width, because
              // the box simply grows to its content instead of clipping it.
              className="w-full min-w-0 overflow-hidden"
            >
              {/* `break-words` is inherited, so it reaches the markdown
                  paragraphs below without restyling the DS `Markdown`. */}
              <div className="pl-6 break-words">
                <ManifestStepBody
                  step={step}
                  automationConfig={automationConfig}
                  onSaveConfigValue={onSaveConfigValue}
                  credentialsByAlias={credentialsByAlias}
                  status={status}
                  io={io}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Flex>
    </Flex>
  );
}
